
import { Component, Ref, Vue } from "vue-property-decorator";
import AppCard from "@/modules/shared/components/AppCard.vue";
import AppInput from "@/modules/shared/components/AppInput.vue";
import AppButton from "@/modules/shared/components/AppButton.vue";
import AppSkeletonLoader from "@/modules/shared/components/AppSkeletonLoader.vue";
import { AbortResponseError } from "@/modules/shared/errors/abort-response.error";
import {
  IdentityProvidersBasicInformation,
  IdentityProvidersBasicInformationData,
  IdentityProvidersButtonInformation,
  IdentityProvidersButtonInformationData,
  IdentityProvidersOpenIdInformation,
  IdentityProvidersOpenIdInformationData,
} from "@/modules/identity-providers/components/IdentityProvidersEdit";
import { ProviderType } from "@flosum-auth/contracts";
import { IdentityProvidersService } from "@/modules/identity-providers/services/identity-providers.service";
import { InjectService } from "@/modules/shared/decorators/inject-service.decorator";
import { IconsService } from "@/modules/shared/services/icons.service";

type ProviderInformationData = IdentityProvidersOpenIdInformationData;

@Component({
  components: {
    AppButton,
    AppCard,
    AppSkeletonLoader,
    AppInput,
    IdentityProvidersBasicInformation,
    IdentityProvidersOpenIdInformation,
    IdentityProvidersButtonInformation,
  },
})
export default class IdentityProvidersEdit extends Vue {
  @InjectService()
  private readonly $identityProvidersService!: IdentityProvidersService;

  @InjectService()
  private readonly $iconsService!: IconsService;

  @Ref()
  public readonly formRef!: HTMLFormElement;

  private readonly identityProviderId = this.$route.params.identityProviderId as string;

  public loading = false;

  public name = "";

  public basicInformation: IdentityProvidersBasicInformationData | null = null;

  public providerInformation: ProviderInformationData | null = null;

  public buttonInformation: IdentityProvidersButtonInformationData | null = null;

  public get isOpenId(): boolean {
    return this.basicInformation?.type === ProviderType.OPEN_ID;
  }

  public async mounted(): Promise<void> {
    try {
      this.loading = true;
      const identityProvider = await this.$identityProvidersService.findById(+this.identityProviderId);

      this.basicInformation = {
        name: identityProvider.name,
        type: identityProvider.type,
      };

      this.buttonInformation = {
        buttonText: identityProvider.buttonText,
        buttonImageId: identityProvider.buttonImageId,
        buttonImageFile: null,
      };

      if (identityProvider.buttonImageId) {
        const { fileName } = await this.$iconsService.findInfo(identityProvider.buttonImageId);
        this.buttonInformation.buttonImageFile = { name: fileName } as File;
      }

      if (identityProvider.providerData) {
        this.providerInformation = identityProvider.providerData;
      } else {
        this.providerInformation = {
          issuer: "",
          clientSecret: "",
          clientId: "",
        };
      }

      this.name = identityProvider.name;
    } catch (error) {
      if (error instanceof AbortResponseError) {
        return;
      }

      await this.$router.replace({
        name: "IdentityProvidersIndex",
      });
    } finally {
      this.loading = false;
    }
  }

  public async edit(): Promise<void> {
    const isFormValid = await this.formRef.validate();

    if (!isFormValid) {
      return;
    }

    const { basicInformation, providerInformation, buttonInformation } = this;

    if (!basicInformation || !providerInformation || !buttonInformation) {
      return;
    }

    const { buttonImageFile, buttonImageId } = buttonInformation;
    let { buttonText } = buttonInformation;

    if (!buttonText) {
      buttonText = `Connect ${basicInformation.name}`;
    }

    const body = {
      ...basicInformation,
      buttonText,
      buttonImageId,
      providerData: providerInformation,
    };

    try {
      if (buttonImageFile?.size) {
        const { id } = await this.$iconsService.upload(buttonImageFile, buttonImageId);
        body.buttonImageId = id;
      }

      await this.$identityProvidersService.update(+this.identityProviderId, body);
      this.$notify({
        text: "Identity Provider was successfully updated",
        type: "success",
      });
      await this.$router.push({
        name: "IdentityProvidersShow",
      });
    } catch (error) {
      if (error instanceof AbortResponseError) {
        return;
      }

      await this.$router.replace({
        name: "IdentityProvidersIndex",
      });
    }
  }

  public async cancel(): Promise<void> {
    await this.$router.push({
      name: "IdentityProvidersShow",
    });
  }
}
